import * as React from 'react';
import { useState, useEffect } from 'react';
import { Rule, Condition, ChainOperator, ComparisonOperator, ArithmeticOperator, Action, Expression } from './types';
import {
  Box,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Autocomplete,
  Tooltip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddBoxIcon from '@mui/icons-material/AddBox';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import HttpClient from '../../providers/HttpClient';
import ApiResources from '../../providers/ApiResources';
import { queryString } from '../../common/UtilFunctions';
import { ChainOperatorEnum } from '../../common/enum';
import { PriceRuleActionValue } from './';

interface Props {
  id: string;
  rule: Rule;
  onChange: (r: Rule) => void;
  onRemove: (r: Rule) => void;
}

const operators: ComparisonOperator[] = ['>', '<', '=', '>=', '<=', '!='];
const chainOperators: ChainOperator[] = ['&&', '||'];
const actionOperations: ArithmeticOperator[] = ['+', '-', '*', '/'];

export const PriceRuleRow: React.FC<Props> = ({ id, rule, onChange, onRemove }) => {
  const { setNodeRef, attributes, listeners, transform, transition, isDragging } = useSortable({ id });
  const [attributeList, setAttributeList] = useState([]);

  useEffect(() => {
    if (attributeList.length === 0) {
      const query = {
        page: 1,
        limit: 1000,
    };

      HttpClient(ApiResources.getProductAttributes(queryString(query)), {
        method: 'GET'
      }).then(({ json }) => {
        setAttributeList(json.results);

        return;
      });
    }
  }, []);

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    touchAction: 'none',
    userSelect: isDragging ? 'none' : undefined,
  };

  const updateCondition = (idx: number, field: keyof Condition, value: any) => {
    if (field === 'chainOperator' && actionOperations.includes(value as Action['operation'])) {
      const action = rule.action;
      action.operation = value;

      onChange({ ...rule, action: action });

      return
    }

    const conditions = [...rule.conditions];

    if (field === 'chainOperator' && !conditions[idx]['chainOperator'] && chainOperators.includes(value as ChainOperator)) {
      conditions.splice(idx + 1, 0, {
        attribute: '',
        operator: '=',
        value: '',
      });
    }

    conditions[idx] = { ...conditions[idx], [field]: value };

    onChange({ ...rule, conditions: conditions });
  };

  const handleAddAdditonalAttribute = (idx: number) => {
    const conditions = [...rule.conditions];

    if (isExpression(conditions[idx]['attribute'])) {
      conditions[idx]['attribute'][conditions[idx]['attribute'].length - 1]['operator'] = '+';
      conditions[idx]['attribute'] = [
        ...conditions[idx]['attribute'] as Expression[],
        {
          name: ''
        }
      ];

      onChange({ ...rule, conditions: conditions });

      return;
    }

    conditions[idx] = {
      ...conditions[idx], attribute: [
        {
          name: conditions[idx]['attribute'] !== '' ? conditions[idx]['attribute'] as string : '',
          operator: '+'
        },
        {
          name: ''
        }
      ]
    };

    onChange({ ...rule, conditions: conditions });
  }

  const isExpression = (attr: any): attr is Expression[] => Array.isArray(attr);
  const updateAction = (field: keyof Action, value: any) => {
    onChange({ ...rule, action: { ...rule.action, [field]: value } });
  };

  const handleRemove = () => {
    onRemove(rule);
  };

  return (
    <Box ref={setNodeRef} className="sortable-price-row" style={style} {...attributes}>
      <Box className="sortable-price-row-draggable" {...listeners} >
        <DragIndicatorIcon />
      </Box>
      <Box display="flex" flexWrap="wrap" gap={1} flex={1}>
        {rule.conditions.map((cond, i) => (
          <React.Fragment key={i}>
            <Box className="price-row-section-attribute">
              {isExpression(cond.attribute) ? (
                <>
                  {cond.attribute.map((expr, j) => (
                    <React.Fragment key={j}>
                      <Autocomplete
                        size="small"
                        disablePortal
                        options={attributeList}
                        value={attributeList.find(attr => attr.code === expr.name) || null}
                        isOptionEqualToValue={(option, value) => option.code === value.code}
                        getOptionLabel={(option) => option.name}
                        getOptionKey={(option) => option.code}
                        onChange={(e, value) => {
                          const updated = [...cond.attribute as Expression[]];
                          updated[j] = { ...updated[j], name: value?.code || '' };
                          updateCondition(i, 'attribute', updated);
                        }}
                        sx={{ width: 200 }}
                        renderInput={(params) => <TextField {...params} />}
                      />

                      {expr.operator && (
                        <Select
                          size="small"
                          value={expr.operator}
                          onChange={(e) => {
                            const updated = [...cond.attribute as Expression[]];
                            updated[j] = { ...updated[j], operator: e.target.value as ArithmeticOperator };
                            updateCondition(i, 'attribute', updated);
                          }}
                        >
                          {actionOperations.map((op) => (
                            <MenuItem key={op} value={op}>{op}</MenuItem>
                          ))}
                        </Select>
                      )}

                      {j === cond.attribute.length - 1 &&
                        <Tooltip title="Attribute">
                          <AddBoxIcon fontSize="small" className="price-row-section-attribute-button"
                            onClick={() => handleAddAdditonalAttribute(i)}
                          />
                        </Tooltip>
                      }
                    </React.Fragment>
                  ))}
                </>
              ) : (
                <>
                  <Autocomplete
                    size="small"
                    disablePortal
                    options={attributeList}
                    value={attributeList.find(attr => attr.code === cond.attribute) || null}
                    isOptionEqualToValue={(option, value) => option.code === value.code}
                    getOptionLabel={(option) => option.name}
                    getOptionKey={(option) => option.code}
                    onChange={(e, value) => updateCondition(i, 'attribute', value?.code || '' )}
                    sx={{ width: 200 }}
                    renderInput={(params) => <TextField {...params} />}
                  />
                  <Tooltip title="Attribute">
                    <AddBoxIcon fontSize="small" className="price-row-section-attribute-button"
                      onClick={() => handleAddAdditonalAttribute(i)}
                    />
                  </Tooltip>
                </>
              )}

              <Select
                size="small"
                value={cond.operator}
                onChange={e => updateCondition(i, 'operator', e.target.value)}
                sx={{ minWidth: 70 }}
              >
                {operators.map(op => (
                  <MenuItem key={op} value={op}>{op}</MenuItem>
                ))}
              </Select>

              <TextField
                size="small"
                value={cond.value}
                onChange={e => updateCondition(i, 'value', e.target.value)}
                sx={{ width: 200 }}
              />
            </Box>

            {i < rule.conditions.length - 1 ||
              cond.chainOperator ||
              (!cond.chainOperator && !rule.action.operation) ? (
              <Box className="price-row-section-operation">
                <Select
                  value={cond.chainOperator ?? ''}
                  onChange={e => updateCondition(i, 'chainOperator', e.target.value)}
                  displayEmpty
                  size="small"
                  sx={{ minWidth: 70 }}
                >
                  <MenuItem value="" disabled>Veza</MenuItem>
                  {ChainOperatorEnum.map(op => (
                    <MenuItem key={op.id} value={op.id}>{op.name}</MenuItem>
                  ))}
                  {!cond.chainOperator &&
                    actionOperations.map(op => (
                      <MenuItem key={op} value={op}>{op}</MenuItem>
                    ))
                  }
                </Select>
              </Box>
            ) : null}

          </React.Fragment>
        ))}

        {rule.action.operation && (
          <>
          <Box className="price-row-section-operation-final">
            <Select
              size="small"
              value={rule.action.operation}
              onChange={e => updateAction('operation', e.target.value)}
              sx={{ minWidth: 70 }}
            >
              {actionOperations.map(op => (
                <MenuItem key={op} value={op}>{op}</MenuItem>
              ))}
            </Select>
          </Box>
            <PriceRuleActionValue
              values={rule.action.values}
              onChange={value => updateAction('values', value)}
              attributeList={attributeList}
            />
          </>
        )}
      </Box>

      <IconButton onClick={handleRemove}>
        <DeleteIcon />
      </IconButton>
    </Box>
  );
};
